import {DragDropContext} from '@wandb/weave/common/containers/DragDropContainer';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import {isEqual} from 'lodash';
import React, {useContext, useEffect, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {useHasBeenOnScreen, useSelector} from '../../../state/hooks';
import {usePanelComments} from '../../../state/reports/usePanelComments';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {Ref as PanelBankSectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {OrganizedSettings} from '../../PanelBank/types';
import {PanelCompRedux} from '../../PanelCompRedux';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds} from '../hooks/hooks';
import {PanelContextProvider, usePanelContext} from '../PanelContextProvider';
import {PanelEditorContainer} from '../PanelEditor/PanelEditorContainer';
import {PanelIDContextProvider} from '../PanelIDContext';
import {SharedPanelStateContextProvider} from '../SharedPanelStateContext';
import {PanelPopupMode} from '../types';
import {WorkspacePanelHeader} from './WorkspacePanelHeader';

type WorkspacePanelContainerProps = {
  defaultMoveToSectionRef?: PanelBankSectionRef;
  inheritedSettings: OrganizedSettings;
  isProjectReadOnly: boolean | undefined;
  isReadOnly: boolean | undefined;
  onContentHeightChange?: (height: number) => void;
  panelBankSectionConfigRef: PanelBankSectionRef;
  panelRef: PanelRef;
  shouldRenderContent: boolean;
};

export const WorkspacePanelContainer = ({
  defaultMoveToSectionRef,
  inheritedSettings,
  isProjectReadOnly,
  isReadOnly,
  onContentHeightChange,
  panelBankSectionConfigRef,
  panelRef,
  shouldRenderContent,
}: WorkspacePanelContainerProps) => {
  const {ref: domRef, hasBeenOnScreen} = useHasBeenOnScreen();

  // This is currently used to track panel setting analytics
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].__id__
  );

  return (
    <div ref={domRef} style={{height: '100%', width: '100%'}}>
      {hasBeenOnScreen ? (
        <PanelContextProvider
          inheritedSettings={inheritedSettings}
          isReadOnly={isReadOnly}
          isProjectReadOnly={isProjectReadOnly}
          panelBankSectionConfigRef={panelBankSectionConfigRef}
          panelRef={panelRef}>
          <PanelIDContextProvider stablePanelId={stablePanelId}>
            <WorkspacePanel
              defaultMoveToSectionRef={defaultMoveToSectionRef}
              shouldRenderContent={shouldRenderContent}
              onContentHeightChange={onContentHeightChange}
            />
          </PanelIDContextProvider>
        </PanelContextProvider>
      ) : (
        <div className="h-full w-full border border-moon-300 bg-white" />
      )}
    </div>
  );
};

export const WorkspacePanel = ({
  defaultMoveToSectionRef,
  onContentHeightChange,
  shouldRenderContent,
}: {
  defaultMoveToSectionRef?: PanelBankSectionRef;
  onContentHeightChange?: (height: number) => void;
  shouldRenderContent?: boolean;
}) => {
  const {isReadOnly, panelRef, panel, setPanelPopupMode} = usePanelContext();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const panelId = searchParams.get('panelId');

  const configXBounds = useConfigXBounds(panelRef);

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  // Open panel editor only if panelId matches the current panel's ID
  useEffect(() => {
    if (panelId === panel.__id__) {
      setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    } else {
      setPanelPopupMode(undefined);
    }
  }, [panelId, panel.__id__, setPanelPopupMode]);

  const {isHighlighted} = usePanelComments({panelRef});

  const {dragRef} = useContext(DragDropContext);
  const isSelected = useMemo(
    () => dragRef?.id === panelRef.id,
    [panelRef, dragRef]
  );

  const panelViewTypeClass = useMemo(
    () => panelType.toLowerCase().replace(' ', '-') + '-container',
    [panelType]
  );

  const panelClassNames = useMemo(() => {
    return classNames('editable-panel', panelViewTypeClass, {
      'panel-selected': isSelected,
      'panel-highlighted': isHighlighted,
      'editable-panel-no-content': !shouldRenderContent,
    });
  }, [panelViewTypeClass, isSelected, isHighlighted, shouldRenderContent]);

  const {targetPanelRef} = useWorkspaceDrawerContext();
  const isWorkspaceDrawerTarget = useMemo(
    () => isEqual(panelRef, targetPanelRef),
    [panelRef, targetPanelRef]
  );

  return (
    <SharedPanelStateContextProvider config={configXBounds}>
      <PanelEditorContainer currentHeight={0} />
      <TailwindContents>
        <div
          className={twMerge(
            panelClassNames,
            'px-12 pb-12 pt-4',
            isWorkspaceDrawerTarget &&
              'border-gold-350 shadow-[0px_0px_0px_4px_rgb(255,230,168)]'
          )}
          data-test="panel">
          <WorkspacePanelHeader
            defaultMoveToSectionRef={defaultMoveToSectionRef}
          />
          <div
            className={twMerge(
              'flex flex-col',
              'h-full w-full grow-[1]',
              'justify-center',
              // Applying this to other panels may cause infinite ResizeObserver loop. Please proceed with caution
              // if you are loosening this check.
              (panelType === 'Code Comparer' ||
                panelType === 'Media Browser' ||
                panelType === 'Markdown Panel') &&
                'overflow-auto'
            )}
            id={'editable-panel-' + panel.__id__}>
            <PanelCompRedux
              configMode={false}
              currentHeight={0}
              dimensions={undefined as any}
              onContentHeightChange={onContentHeightChange}
              panelRef={panelRef}
              readOnly={isReadOnly}
            />
          </div>
        </div>
      </TailwindContents>
    </SharedPanelStateContextProvider>
  );
};
